import { Point, Polygon as AppPolygon, Line, Layer, GlobalContext } from "../../../context/context";
import { pickRouteColor, OsmRouteMetadata } from "../../../types/osmRoutes";
import { OpenStreetMapService } from "../../../services/osm/OpenStreetMapService";
import * as turf from '@turf/turf';
import { Feature, Polygon, MultiPolygon } from 'geojson';
import { v4 as uuidv4 } from 'uuid';
import union from '@turf/union';
import * as polygonClipping from 'martinez-polygon-clipping';
import { logEvent } from '../../../services/analytics';
import { getAtlasApiBaseUrl } from '../../../services/getAtlasApiBaseUrl';
import { atlasFetch } from '../../../services/api/atlasBackendClient';
import { getApiErrorDetail } from '../../../utils/apiErrorDetail';
import { pointInPolygon } from '../components/generate-rides/utils';

interface TransitAccessibilityHandlerProps {
  polygon: AppPolygon;
  global: GlobalContext;
  setTransitLoading: (loading: boolean) => void;
  setTransitError: (error: string | null) => void;
}

// Initialize OSM service with configuration
const osmService = new OpenStreetMapService({
  baseUrl: 'https://overpass-api.de',
  rateLimit: {
    requestsPerMinute: 30,
    requestsPerHour: 500,
  },
  cache: {
    ttl: 3600000, // 1 hour
    maxSize: 50,
  },
  timeout: 30000,
});

// Helper: batch union for martinez-polygon-clipping
function batchUnion(polys: any[][][], batchSize = 10) {
  let results = [];
  for (let i = 0; i < polys.length; i += batchSize) {
    const batch = polys.slice(i, i + batchSize);
    // @ts-ignore
    const batchUnioned = polygonClipping.union(...batch);
    if (Array.isArray(batchUnioned)) {
      results.push(batchUnioned);
    }
  }
  // Now union the batch results
  // @ts-ignore
  return polygonClipping.union(...results);
}

// Helper functions for stop styling
function getStopColor(stopType: string): string {
  switch (stopType) {
    case 'bus': return '#1976d2';
    case 'train': return '#388e3c';
    case 'tram': return '#f57c00';
    case 'subway': return '#7b1fa2';
    default: return '#757575';
  }
}

function getStopIcon(stopType: string): 'bus' | 'train' | 'tram' | 'subway' | 'location' {
  switch (stopType) {
    case 'bus': return 'bus';
    case 'train': return 'train';
    case 'tram': return 'tram';
    case 'subway': return 'subway';
    default: return 'location';
  }
}

function buildOverpassPolyString(polygon: AppPolygon): string {
  return polygon.coordinates[0].map(([lat, lng]) => `${lat} ${lng}`).join(' ');
}

async function fetchKeyLocationPois(polygon: AppPolygon, analysisRunId: string): Promise<Point[]> {
  const poly = buildOverpassPolyString(polygon);
  const query = `
[out:json][timeout:40];
(
  nwr["amenity"="school"](poly:"${poly}");
  nwr["amenity"="hospital"](poly:"${poly}");
  nwr["amenity"="library"](poly:"${poly}");
  nwr["shop"="supermarket"](poly:"${poly}");
);
out center tags;
`;

  const response = await fetch('https://overpass-api.de/api/interpreter', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
    body: `data=${encodeURIComponent(query)}`,
  });
  if (!response.ok) return [];
  const data = await response.json() as { elements?: any[] };
  const elements = data.elements ?? [];

  const toPoint = (element: any, index: number): Point | null => {
    const lat = typeof element.lat === 'number' ? element.lat : element.center?.lat;
    const lng = typeof element.lon === 'number' ? element.lon : element.center?.lon;
    if (typeof lat !== 'number' || typeof lng !== 'number') return null;
    if (!pointInPolygon([lat, lng], polygon.coordinates[0])) return null;
    const tags = element.tags ?? {};
    const amenity = String(tags.amenity || '').toLowerCase();
    const shop = String(tags.shop || '').toLowerCase();
    const leisure = String(tags.leisure || '').toLowerCase();
    const name = tags.name || tags['name:en'] || `${amenity || shop || leisure || 'location'} ${index + 1}`;

    let icon: Point['icon'] = 'location';
    let colour = '#6d4c41';
    if (amenity === 'school') { icon = 'school'; colour = '#1565c0'; }
    else if (amenity === 'hospital') { icon = 'hospital'; colour = '#c62828'; }
    else if (shop === 'supermarket') { icon = 'store'; colour = '#2e7d32'; }
    else if (amenity === 'library') { icon = 'location'; colour = '#5e35b1'; }

    return {
      id: `osm-poi-${analysisRunId}-${element.type}-${element.id}`,
      type: 'point',
      name,
      description: `Key location (${amenity || shop || leisure || 'poi'})`,
      colour,
      icon,
      size: 24,
      isLocked: false,
      coordinates: [lat, lng],
      creationSource: 'osm-key-location',
    };
  };

  const points = elements.map(toPoint).filter((p): p is Point => p !== null);
  const dedup = new Map<string, Point>();
  for (const p of points) dedup.set(`${p.coordinates[0].toFixed(6)},${p.coordinates[1].toFixed(6)}`, p);
  return Array.from(dedup.values());
}

/**
 * Clips a route's [lat, lng] geometry to the analysis polygon's bbox (padded
 * a little so routes that briefly loop just outside the area still show
 * their in-area portion). If the route is wholly inside the bbox it's
 * returned unchanged; if it produces multiple disjoint segments after
 * clipping, the longest segment is kept (good-enough for v1 — a long-haul
 * intercity route passing through the area gets its single arc through).
 * Returns [] when no segment survives.
 */
function clipRouteToBbox(
  geometry: [number, number][],
  paddedBbox: [number, number, number, number] // [minLng, minLat, maxLng, maxLat]
): [number, number][] {
  if (geometry.length < 2) return [];
  const line = turf.lineString(geometry.map(([lat, lng]) => [lng, lat]));
  // @ts-ignore — turf typings expect a BBox literal, not a tuple alias
  const clipped = turf.bboxClip(line, paddedBbox);
  const geom: any = clipped.geometry;
  if (!geom) return [];
  if (geom.type === 'LineString') {
    return (geom.coordinates as [number, number][]).map(([lng, lat]) => [lat, lng]);
  }
  if (geom.type === 'MultiLineString') {
    let best: [number, number][] = [];
    for (const seg of geom.coordinates as [number, number][][]) {
      if (seg.length > best.length) best = seg;
    }
    return best.map(([lng, lat]) => [lat, lng]);
  }
  return [];
}

// Helper to check if an array is a valid ring (array of [number, number])
function isRing(arr: any): arr is [number, number][] {
  return Array.isArray(arr) && arr.length > 3 && arr.every(pt => Array.isArray(pt) && pt.length === 2 && typeof pt[0] === 'number' && typeof pt[1] === 'number');
}

// Helper to flatten unionedCoords to an array of rings
function extractRings(unioned: any): [number, number][][] {
  if (!Array.isArray(unioned)) return [];
  // If it's a single ring
  if (isRing(unioned)) return [unioned];
  // If it's an array of rings
  if (Array.isArray(unioned[0]) && isRing(unioned[0])) return unioned;
  // If it's an array of arrays of rings (MultiPolygon)
  if (Array.isArray(unioned[0]) && Array.isArray(unioned[0][0]) && isRing(unioned[0][0])) {
    return unioned.flat();
  }
  // Otherwise, try to flatten one level and filter valid rings
  return unioned.flat().filter(isRing);
}

// Prepare polygons for population analysis (flatten MultiPolygons)
function flattenToGeoJSONPolygons(geo: any): any[] {
  if (!geo) return [];
  if (geo.type === 'Polygon') {
    return [{ type: 'Polygon', coordinates: geo.coordinates }];
  } else if (geo.type === 'MultiPolygon') {
    return geo.coordinates.map((coords: any) => ({ type: 'Polygon', coordinates: coords }));
  }
  return [];
}

// Helper to swap [lat, lng] to [lng, lat] for GeoJSON
function swapLatLngToLngLat(polygon: any) {
  return {
    ...polygon,
    coordinates: polygon.coordinates.map((ring: any) =>
      ring.map(([lat, lng]: [number, number]) => [lng, lat])
    )
  };
}

// Helper to swap [lat, lng] to [lng, lat] for GeoJSON
function swapLatLngToLngLatSingle(geo: any) {
  if (!geo) return geo;
  if (geo.type === 'Polygon') {
    return {
      ...geo,
      coordinates: geo.coordinates.map((ring: any) =>
        ring.map(([lat, lng]: [number, number]) => [lng, lat])
      )
    };
  } else if (geo.type === 'MultiPolygon') {
    return {
      ...geo,
      coordinates: geo.coordinates.map((poly: any) =>
        poly.map((ring: any) => ring.map(([lat, lng]: [number, number]) => [lng, lat]))
      )
    };
  }
  return geo;
}

export async function handleTransitAccessibilityAnalysis({
  polygon,
  global,
  setTransitLoading,
  setTransitError
}: TransitAccessibilityHandlerProps): Promise<{ coveredPopulation: number, uncoveredPopulation: number }> {
  const startTime = Date.now();
  const analysisRunId = uuidv4();
  setTransitLoading(true);
  setTransitError(null);
  
  try {
    console.log('Starting transit accessibility analysis...');
    
    logEvent({
      category: 'Transit Analysis',
      action: 'TransitAccessibilityAnalysisInitiated',
      label: polygon.name || 'Unnamed Polygon',
      polygon_id: polygon.id,
      polygon_vertex_count: polygon.coordinates[0]?.length || 0
    });
    
    // 1. Fetch OSM transit stops within the polygon (app Polygon type).
    // Routes are fetched in parallel via a separate (heavier) Overpass query so
    // the stops/coverage pipeline doesn't have to wait for them. Failures on
    // the routes side are non-fatal — coverage analysis only needs stops.
    const routesPromise = osmService.getTransitRoutes(polygon).catch(err => {
      console.warn('OSM routes fetch failed:', err);
      return null;
    });
    const transitData = await osmService.getTransitData(polygon);
    console.log('Fetched transit data:', {
      busStops: transitData.stops.bus.length,
      trainStops: transitData.stops.train.length,
      tramStops: transitData.stops.tram.length,
      subwayStops: transitData.stops.subway.length,
    });
    
    // 2. Combine all stops into a single array
    const allStops = [
      ...transitData.stops.bus,
      ...transitData.stops.train,
      ...transitData.stops.tram,
      ...transitData.stops.subway,
    ];
    
    if (allStops.length === 0) {
      logEvent({
        category: 'Transit Analysis',
        action: 'TransitAccessibilityAnalysisNoStops',
        label: polygon.name || 'Unnamed Polygon',
        polygon_id: polygon.id,
        error_message: 'No transit stops found in the selected area'
      });
      throw new Error('No transit stops found in the selected area');
    }
    
    // 3. Convert OSM stops to Point shapes
    const stopPoints: Point[] = allStops.map(stop => ({
      id: `osm-stop-${analysisRunId}-${stop.id}`,
      type: 'point',
      name: stop.name,
      description: `${stop.type} stop`,
      colour: getStopColor(stop.type),
      icon: getStopIcon(stop.type),
      size: 24,
      isLocked: false,
      coordinates: stop.coordinates,
      // Tags this point as part of the OSM transit-stop result set so the
      // Layer Panel can collapse it into a single grouped accordion entry.
      creationSource: 'transit-osm-stop',
    }));
    
    // Fetch non-transit key locations within the polygon (schools, hospitals,
    // supermarkets, libraries). Failures are non-fatal.
    const keyLocationPois = await fetchKeyLocationPois(polygon, analysisRunId).catch(() => [] as Point[]);

    // 4. Create 400m buffers for each stop
    const bufferFeatures: any[] = [];
    
    for (const stop of allStops) {
      try {
        const point = turf.point([stop.coordinates[1], stop.coordinates[0]]); // [lng, lat]
        const buffer = turf.buffer(point, 0.4, { units: 'kilometers' });
        if (buffer && buffer.geometry && buffer.geometry.type === 'Polygon') {
          bufferFeatures.push(buffer);
        }
      } catch (error) {
        console.warn(`Failed to create buffer for stop ${stop.id}:`, error);
      }
    }
    
    // --- NEW: Union all buffer polygons using backend API ---
    let unionedBufferPolygon: AppPolygon | null = null;
    let unionedGeometry: any = null; // Store the raw unioned geometry
    try {
      // Prepare GeoJSON Feature objects for backend (not just geometry)
      const geojsonFeatures = bufferFeatures.map((buffer) => ({
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: buffer.geometry.coordinates
        },
        properties: {}
      }));
      
      console.log('Sending to union endpoint:', {
        featureCount: geojsonFeatures.length,
        sampleFeature: geojsonFeatures[0]
      });
      
      const unionResponse = await atlasFetch(`${getAtlasApiBaseUrl()}/api/union-buffers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ polygons: geojsonFeatures })
      });
      
      if (!unionResponse.ok) {
        const errorText = await getApiErrorDetail(
          unionResponse,
          `Failed to union buffer polygons: ${unionResponse.status}`
        );
        console.error('Union endpoint error:', {
          status: unionResponse.status,
          statusText: unionResponse.statusText,
          errorText: errorText
        });
        throw new Error(`Failed to union buffer polygons: ${unionResponse.status} - ${errorText}`);
      }
      
      const unionData = await unionResponse.json();
      unionedGeometry = unionData.geometry; // Store the raw geometry
      console.log('Union response:', unionData);
      
      // Convert GeoJSON (Multi)Polygon to AppPolygon
      let coords: [number, number][][] = [];
      if (unionData.geometry.type === 'Polygon') {
        coords = unionData.geometry.coordinates.map(
          (ring: [number, number][]) => ring.map(([lng, lat]: [number, number]) => [lat, lng])
        );
      } else if (unionData.geometry.type === 'MultiPolygon') {
        // For MultiPolygon, we want to preserve the structure but convert coordinates
        coords = unionData.geometry.coordinates.flatMap((poly: [number, number][][]) => 
          poly.map((ring: [number, number][]) => 
            ring.map(([lng, lat]: [number, number]) => [lat, lng])
          )
        );
      }
      
      unionedBufferPolygon = {
        id: uuidv4(),
        type: 'polygon',
        name: 'Unioned Transit Buffer (400m)',
        colour: '#00bcd4',
        description: `Unioned 400m buffer around ${geojsonFeatures.length} transit stops`,
        opacity: '0.35',
        borderColour: '#00838f',
        borderWidth: 3,
        isLocked: false,
        coordinates: coords,
      };
    } catch (err) {
      console.warn('Unioning buffer polygons failed:', err);
    }

    // 7. Everything produced by the analysis (buffer, uncovered area, stops)
    // is appended into the host layer of the analysis polygon so the user
    // gets one tidy "analysis result" grouped with the polygon. The Layer
    // Panel collapses tagged stop points into a single accordion entry.
    const nextLayerId =
      global.mapLayers.length > 0 ? Math.max(...global.mapLayers.map((layer) => layer.layerId)) + 1 : 0;

    // Shapes that should land in the analysis polygon's host layer. Order
    // matters — they render top-to-bottom in the panel as added.
    const shapesForPolygonLayer: (AppPolygon | Point | Line)[] = [];
    let uncoveredAreaPolygon: AppPolygon | null = null;

    if (unionedBufferPolygon && unionedGeometry) {
      shapesForPolygonLayer.push(unionedBufferPolygon);

      // --- NEW: Call backend subtraction endpoint to get uncovered area ---
      try {
        // Prepare GeoJSON for subtraction - ensure consistent [lng, lat] ordering
        const basePolygonGeoJSON = {
          type: "Polygon",
          coordinates: polygon.coordinates
        };
        
        // Use the raw unioned geometry for subtraction to preserve structure
        const subtractResponse = await atlasFetch(`${getAtlasApiBaseUrl()}/api/subtract-polygons`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            basePolygon: swapLatLngToLngLatSingle(basePolygonGeoJSON),
            subtractPolygon: unionedGeometry // Use the stored raw geometry
          })
        });
        if (subtractResponse.ok) {
          const subtractData = await subtractResponse.json();
          if (subtractData.result) {
            // Convert GeoJSON to AppPolygon - swap back to [lat, lng] for frontend
            const resultLngLat = subtractData.result;
            let coords: [number, number][][] = [];
            if (resultLngLat.type === 'Polygon') {
              coords = resultLngLat.coordinates.map(
                (ring: [number, number][]) => ring.map(([lng, lat]: [number, number]) => [lat, lng])
              );
            } else if (resultLngLat.type === 'MultiPolygon') {
              // For each polygon in the MultiPolygon, preserve all rings (exterior and holes)
              coords = resultLngLat.coordinates.reduce((acc: [number, number][][], poly: [number, number][][]) => {
                // Convert all rings in this polygon (exterior and holes)
                const convertedRings = poly.map(
                  (ring: [number, number][]) => ring.map(([lng, lat]: [number, number]) => [lat, lng])
                );
                return [...acc, ...convertedRings];
              }, []);
            }
            uncoveredAreaPolygon = {
              id: uuidv4(),
              type: 'polygon',
              name: 'Uncovered Area (Transit Desert)',
              colour: '#e53935',
              description: 'Area not covered by transit buffers',
              opacity: '0.45',
              borderColour: '#b71c1c',
              borderWidth: 2,
              isLocked: false,
              coordinates: coords,
            };
            shapesForPolygonLayer.push(uncoveredAreaPolygon);
          }
        } else {
          const errorText = await getApiErrorDetail(
            subtractResponse,
            `Subtraction failed: ${subtractResponse.status}`
          );
          console.warn('Subtraction endpoint error:', errorText);
        }
      } catch (err) {
        console.warn('Subtraction of polygons failed:', err);
      }
    }

    // Stops come last so they render below the coverage/uncovered polygons
    // and route lines in the Layer Panel.
    shapesForPolygonLayer.push(...stopPoints);
    shapesForPolygonLayer.push(...keyLocationPois);

    // Merge stops + coverage into the analysis polygon's host layer first so
    // the user sees them as soon as they're ready. Routes arrive separately
    // below. Defensive fallback creates a dedicated layer if the host can't
    // be found.
    global.setLayers(prev => {
      const hostIdx = prev.findIndex(l => l.shapes.some(s => s.id === polygon.id));
      if (hostIdx >= 0) {
        return prev.map((l, i) => i === hostIdx ? { ...l, shapes: [...l.shapes, ...shapesForPolygonLayer] } : l);
      }
      return [...prev, {
        layerId: nextLayerId,
        layerName: 'Population Analysis Results',
        hidden: false,
        shapes: shapesForPolygonLayer,
      }];
    });

    console.log(`✅ Added ${stopPoints.length} transit stops, ${keyLocationPois.length} key locations and ${bufferFeatures.length} buffer polygons to the analysis layer`);

    // Routes append asynchronously — they don't gate any downstream work, so
    // fire-and-forget. The Layer Panel groups them under an "OSM Transit
    // Routes" accordion via the osmRouteMetadata marker.
    void routesPromise.then(routesData => {
      if (!routesData) return;
      // Compute a slightly padded bbox of the analysis polygon. Each route's
      // geometry is clipped to this box so long-haul intercity relations
      // (e.g. inter-city coach routes returned because they intersect the
      // bbox) don't render hundreds of km outside the area of interest.
      // Padding ≈ 5km north/south, scaled for longitude at the polygon's
      // mid-latitude.
      const lats = polygon.coordinates[0].map(([lat]) => lat);
      const lngs = polygon.coordinates[0].map(([, lng]) => lng);
      const midLat = (Math.min(...lats) + Math.max(...lats)) / 2;
      const latPad = 0.05; // ~5.5km
      const lngPad = 0.05 / Math.max(0.2, Math.cos(midLat * Math.PI / 180));
      const paddedBbox: [number, number, number, number] = [
        Math.min(...lngs) - lngPad,
        Math.min(...lats) - latPad,
        Math.max(...lngs) + lngPad,
        Math.max(...lats) + latPad,
      ];

      const allRoutes = [
        ...routesData.bus,
        ...routesData.tram,
        ...routesData.subway,
        ...routesData.train,
      ];
      const routeLines: Line[] = allRoutes
        .map(r => ({ ...r, geometry: clipRouteToBbox(r.geometry, paddedBbox) }))
        .filter(r => r.geometry.length >= 2)
        .map((r): Line => {
          const colour = pickRouteColor({
            osmColour: r.colour,
            ref: r.ref,
            name: r.name,
            mode: r.osmMode || r.type,
          });
          const metadata: OsmRouteMetadata = {
            type: 'osmRoute',
            osmRouteId: r.id,
            mode: r.osmMode || r.type,
            ref: r.ref,
            routeName: r.name,
            from: r.from,
            to: r.to,
            via: r.via,
            operator: r.operator !== 'Unknown' ? r.operator : undefined,
            network: r.network,
            colour: r.colour,
            openingHours: r.openingHours,
            interval: r.interval,
            duration: r.duration,
            wheelchair: r.wheelchair,
            stopCount: r.stops.length,
            stopShapeIds: r.stops.map(osmStopId => `osm-stop-${analysisRunId}-${osmStopId}`),
          };
          return {
            id: `osm-route-${analysisRunId}-${r.id}`,
            type: 'line',
            name: r.ref ? `${r.ref} — ${r.name}` : r.name,
            description: [r.from && r.to ? `${r.from} → ${r.to}` : null, r.operator !== 'Unknown' ? r.operator : null]
              .filter(Boolean)
              .join(' · '),
            colour,
            borderColour: colour,
            borderWidth: 4,
            opacity: '0.85',
            isLocked: true,
            coordinates: r.geometry,
            osmRouteMetadata: metadata,
          };
        });
      if (routeLines.length === 0) return;
      global.setLayers(prev => {
        const hostIdx = prev.findIndex(l => l.shapes.some(s => s.id === polygon.id));
        if (hostIdx < 0) return prev;
        return prev.map((l, i) => i === hostIdx ? { ...l, shapes: [...l.shapes, ...routeLines] } : l);
      });
      console.log(`✅ Added ${routeLines.length} transit routes to the analysis layer (async)`);
      logEvent({
        category: 'Transit Analysis',
        action: 'TransitRoutesLoaded',
        label: polygon.name || 'Unnamed Polygon',
        polygon_id: polygon.id,
        total_routes: allRoutes.length,
        routes_with_geometry: routeLines.length,
        bus_routes: routesData.bus.length,
        tram_routes: routesData.tram.length,
        subway_routes: routesData.subway.length,
        train_routes: routesData.train.length,
        routes_with_colour: routeLines.filter(l => l.osmRouteMetadata?.colour).length,
        routes_with_operator: routeLines.filter(l => l.osmRouteMetadata?.operator).length,
      });
    });

    // --- NEW: Intersect unioned buffer with original polygon before population analysis ---
    let clippedCoveredGeoJSON = null;
    if (unionedBufferPolygon) {
      const unionedGeoJSON = {
        type: 'Polygon',
        coordinates: unionedBufferPolygon.coordinates
      };
      const basePolygonGeoJSON = {
        type: 'Polygon',
        coordinates: polygon.coordinates
      };
      // Swap to [lng, lat] order for both
      const unionedGeoJSONLngLat = swapLatLngToLngLatSingle(unionedGeoJSON);
      const basePolygonGeoJSONLngLat = swapLatLngToLngLatSingle(basePolygonGeoJSON);
      try {
        const intersectResp = await atlasFetch(`${getAtlasApiBaseUrl()}/api/intersect-polygons`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ polygonA: unionedGeoJSONLngLat, polygonB: basePolygonGeoJSONLngLat })
        });
        if (intersectResp.ok) {
          const intersectData = await intersectResp.json();
          if (intersectData.result) {
            clippedCoveredGeoJSON = intersectData.result;
          }
        } else {
          // If intersection fails, use the unioned buffer directly
          console.warn('Intersection failed, using unioned buffer directly');
          clippedCoveredGeoJSON = unionedGeoJSONLngLat;
        }
      } catch (err) {
        // If intersection fails, use the unioned buffer directly
        console.warn('Intersection failed, using unioned buffer directly:', err);
        clippedCoveredGeoJSON = unionedGeoJSONLngLat;
      }
    }

    // --- Call population API for covered and uncovered areas (single polygon each) ---
    let coveredPopulation = 0;
    let uncoveredPopulation = 0;
    try {
      if (clippedCoveredGeoJSON) {
        console.log('Sending clipped covered area to population API:', JSON.stringify(clippedCoveredGeoJSON));
        const coveredResp = await atlasFetch(`${getAtlasApiBaseUrl()}/api/population-grid`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ polygon: clippedCoveredGeoJSON, debug: true })
        });
        if (!coveredResp.ok) {
          throw new Error(
            await getApiErrorDetail(coveredResp, 'Failed to compute covered population')
          );
        }
        const coveredData = await coveredResp.json();
        coveredPopulation = coveredData.total_population || 0;
      }
      if (uncoveredAreaPolygon) {
        const uncoveredGeoJSON = {
          type: 'Polygon',
          coordinates: uncoveredAreaPolygon.coordinates
        };
        const uncoveredGeoJSONLngLat = swapLatLngToLngLatSingle(uncoveredGeoJSON);
        console.log('Sending uncovered area to population API:', JSON.stringify(uncoveredGeoJSONLngLat));
        const uncoveredResp = await atlasFetch(`${getAtlasApiBaseUrl()}/api/population-grid`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ polygon: uncoveredGeoJSONLngLat, debug: true })
        });
        if (!uncoveredResp.ok) {
          throw new Error(
            await getApiErrorDetail(uncoveredResp, 'Failed to compute uncovered population')
          );
        }
        const uncoveredData = await uncoveredResp.json();
        uncoveredPopulation = uncoveredData.total_population || 0;
      }

      // Get total population for the original polygon
      const originalPolygonGeoJSON = {
        type: 'Polygon',
        coordinates: polygon.coordinates
      };
      const originalPolygonGeoJSONLngLat = swapLatLngToLngLatSingle(originalPolygonGeoJSON);
      const totalResp = await atlasFetch(`${getAtlasApiBaseUrl()}/api/population-grid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ polygon: originalPolygonGeoJSONLngLat, debug: true })
      });
      
      if (!totalResp.ok) {
        throw new Error(
          await getApiErrorDetail(totalResp, 'Failed to compute total population')
        );
      }
      const totalData = await totalResp.json();
      const totalPopulation = totalData.total_population || 0;
      
      // Normalize the covered and uncovered populations to match the total
      if (totalPopulation > 0) {
        const currentTotal = coveredPopulation + uncoveredPopulation;
        if (currentTotal > 0) {
          const scaleFactor = totalPopulation / currentTotal;
          coveredPopulation *= scaleFactor;
          uncoveredPopulation *= scaleFactor;
        } else {
          // If both are 0, assign all population to uncovered
          uncoveredPopulation = totalPopulation;
        }
      }

      // Log for now; next step: display in UI
      console.log('Population covered by transit:', coveredPopulation);
      console.log('Population in transit deserts:', uncoveredPopulation);
    } catch (err) {
      console.warn('Population analysis failed:', err);
    }

    // At the end of the handler, after population analysis
    const endTime = Date.now();
    const duration = endTime - startTime;
    
    logEvent({
      category: 'Transit Analysis',
      action: 'TransitAccessibilityAnalysisCompleted',
      label: polygon.name || 'Unnamed Polygon',
      polygon_id: polygon.id,
      total_stops: allStops.length,
      key_locations: keyLocationPois.length,
      bus_stops: transitData.stops.bus.length,
      train_stops: transitData.stops.train.length,
      tram_stops: transitData.stops.tram.length,
      subway_stops: transitData.stops.subway.length,
      covered_population: coveredPopulation,
      uncovered_population: uncoveredPopulation,
      execution_time_ms: duration,
      shapes_added_to_polygon_layer: shapesForPolygonLayer.length
    });
    
    return {
      coveredPopulation,
      uncoveredPopulation
    };
  } catch (error) {
    const endTime = Date.now();
    const duration = endTime - startTime;
    
    logEvent({
      category: 'Transit Analysis',
      action: 'TransitAccessibilityAnalysisError',
      label: polygon.name || 'Unnamed Polygon',
      polygon_id: polygon.id,
      error_message: error instanceof Error ? error.message : 'Unknown error',
      execution_time_ms: duration
    });
    
    console.error('Error in transit accessibility analysis:', error);
    setTransitError(error instanceof Error ? error.message : 'Failed to analyze transit accessibility');
    throw error;
  } finally {
    setTransitLoading(false);
  }
}
